import moment from "moment"

export type PublicSublayer = {
  id?:number,
  title?:string,
  name?:string,
  is_enabled:boolean,
  metadata?:null
}
export type PublicLayers = {
    url?:string
    name?:string,
    title?: string,
    type?:string,
    version?: string,
    layers?:PublicSublayer[],
    metadata?: {
      title?: string,
      output_format?: string,
      color?: string,
      zoom?: number,
      opacity?: number,
    },
    username?: string,
    password?: string
}

/** Fallback for legacy open_layers rows that never stored metadata.output_format */
export const DEFAULT_WFS_OUTPUT_FORMAT = 'json';

/**
 * Preferred GeoJSON-like outputFormat values, most specific first.
 * Matching is case-insensitive; the server's exact string is returned.
 */
export const WFS_GEOJSON_FORMAT_ALIASES = [
  'application/geo+json',
  'application/json; subtype=geojson',
  'application/json',
  'json',
  'geojson',
] as const;

/**
 * Pick the best GeoJSON-compatible format the server advertises.
 * Returns the exact server string, or null if none match.
 */
export function pickGeoJsonOutputFormat(serverFormats: string[]): string | null {
  if (!Array.isArray(serverFormats) || !serverFormats.length) {
    return null;
  }
  const normalized = serverFormats
    .filter((f): f is string => typeof f === 'string' && f.trim().length > 0)
    .map((f) => ({ raw: f.trim(), key: f.trim().toLowerCase() }));

  for (const alias of WFS_GEOJSON_FORMAT_ALIASES) {
    const match = normalized.find((f) => f.key === alias.toLowerCase());
    if (match) {
      return match.raw;
    }
  }
  return null;
}

/**
 * Resolve outputFormat for a GetFeature request:
 * stored metadata.output_format, else DEFAULT_WFS_OUTPUT_FORMAT ('json').
 */
export function resolveWfsOutputFormat(layer?: { metadata?: { output_format?: string } } | null): string {
  const stored = layer?.metadata?.output_format;
  if (typeof stored === 'string' && stored.trim()) {
    return stored.trim();
  }
  return DEFAULT_WFS_OUTPUT_FORMAT;
}

function asArray<T>(value: T | T[] | undefined | null): T[] {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function collectFormatStrings(node: unknown, formats: string[]): void {
  if (node == null) {
    return;
  }
  if (typeof node === 'string') {
    formats.push(node);
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item) => collectFormatStrings(item, formats));
    return;
  }
  if (typeof node === 'object') {
    Object.values(node as Record<string, unknown>).forEach((value) => collectFormatStrings(value, formats));
  }
}

/**
 * Extract outputFormat / Format values from ngx-xml2json GetCapabilities JSON.
 */
export function collectWfsOutputFormatsFromCapabilitiesJson(capabilitiesRoot: any): string[] {
  const formats: string[] = [];
  if (!capabilitiesRoot || typeof capabilitiesRoot !== 'object') {
    return formats;
  }

  const operationsMetadata =
    capabilitiesRoot['ows:OperationsMetadata'] ||
    capabilitiesRoot['OperationsMetadata'];
  const operations = asArray(
    operationsMetadata?.['ows:Operation'] || operationsMetadata?.['Operation']
  );

  operations.forEach((operation) => {
    const opName = operation?.['@attributes']?.name || operation?.['@attributes']?.Name;
    if (opName && String(opName).toLowerCase() !== 'getfeature') {
      return;
    }
    const parameters = asArray(
      operation?.['ows:Parameter'] || operation?.['Parameter']
    );
    parameters.forEach((parameter) => {
      const paramName = parameter?.['@attributes']?.name || parameter?.['@attributes']?.Name;
      if (String(paramName || '').toLowerCase() !== 'outputformat') {
        return;
      }
      const allowed =
        parameter?.['ows:AllowedValues'] ||
        parameter?.['AllowedValues'] ||
        parameter;
      const values = asArray(allowed?.['ows:Value'] || allowed?.['Value']);
      collectFormatStrings(values, formats);
    });
  });

  const featureTypeList =
    capabilitiesRoot['wfs:FeatureTypeList'] ||
    capabilitiesRoot['FeatureTypeList'];
  const featureTypes = asArray(
    featureTypeList?.['wfs:FeatureType'] || featureTypeList?.['FeatureType']
  );
  featureTypes.forEach((featureType) => {
    const outputFormats =
      featureType?.['wfs:OutputFormats'] ||
      featureType?.['OutputFormats'];
    const legacyFormats = asArray(
      outputFormats?.['wfs:Format'] || outputFormats?.['Format']
    );
    collectFormatStrings(legacyFormats, formats);
  });

  return [...new Set(formats.map((f) => f.trim()).filter(Boolean))];
}
// open data map from the json responce
export function openDataURLXMLMap(jsonObject,publicLayers){
    const layers:PublicLayers=publicLayers;
    const sublayers=[];
    if (!layers.metadata) {
        layers.metadata = {title: ''};
    }
    let key;
    switch(layers.type){
        case 'wfs':
            key = jsonObject['wfs:WFS_Capabilities'] ? 'wfs:WFS_Capabilities' : 'WFS_Capabilities';
            if(jsonObject[key]){
                layers.name = jsonObject[key]['ows:ServiceIdentification'] ? jsonObject[key]['ows:ServiceIdentification']['ows:Title'] ? jsonObject[key]['ows:ServiceIdentification']['ows:Title'] : jsonObject[key]['ows:ServiceProvider']['ows:ProviderName'] : jsonObject[key]['ows:ServiceProvider']['ows:ProviderName']
                layers.metadata['title'] = layers.name;
                layers.version = jsonObject[key]['ows:ServiceIdentification'] ? jsonObject[key]['ows:ServiceIdentification']['ows:ServiceTypeVersion'] ? jsonObject[key]['ows:ServiceIdentification']['ows:ServiceTypeVersion'] : jsonObject[key]['@attributes']['version'] : jsonObject[key]['@attributes']['version']
                const serverFormats = collectWfsOutputFormatsFromCapabilitiesJson(jsonObject[key]);
                layers.metadata.output_format =
                  pickGeoJsonOutputFormat(serverFormats) || DEFAULT_WFS_OUTPUT_FORMAT;
                // Handle namespaced and non-namespaced FeatureTypeList
                const featureTypeList = jsonObject[key]['wfs:FeatureTypeList'] || jsonObject[key]['FeatureTypeList'];
                if(featureTypeList){
                    // Handle namespaced and non-namespaced FeatureType
                    const featureType = featureTypeList['wfs:FeatureType'] || featureTypeList['FeatureType'];
                    if(featureType){
                        if(Array.isArray(featureType)){
                            featureType.forEach(layer => {
                                // Handle namespaced and non-namespaced property names
                                const title = layer['wfs:Title'] || layer['Title'] || layer['ows:Title'];
                                const name = layer['wfs:Name'] || layer['Name'];
                                sublayers.push({title: title, name: name, is_enabled: false, metadata: null});
                            });
                        }else{
                            // Single feature type
                            const title = featureType['wfs:Title'] || featureType['Title'] || featureType['ows:Title'];
                            const name = featureType['wfs:Name'] || featureType['Name'];
                            sublayers.push({title: title, name: name, is_enabled: false, metadata: null});
                        }
                    }
                }
                layers.layers=sublayers;
            }
            break;
        case 'wms':
            key = jsonObject['wms:WMS_Capabilities'] ? 'wms:WMS_Capabilities' : 'WMS_Capabilities';
            if(jsonObject[key]){
                layers.name=jsonObject[key].Service.Title;
                layers.metadata['title'] = layers.name;
                layers.version=jsonObject[key]['@attributes'].version;
                if(Array.isArray(jsonObject[key].Capability.Layer.Layer)){
                    jsonObject[key].Capability.Layer.Layer.forEach(layer => {
                        sublayers.push({title:layer.Name,name:layer.Name,is_enabled:false,metadata:null});
                    });
                }else if(jsonObject[key].Capability.Layer.Layer){
                    const singleLayer=jsonObject[key].Capability.Layer.Layer;
                    sublayers.push({title:singleLayer.Name,name:singleLayer.Name,is_enabled:false,metadata:null});
                }else{
                    const singleLayer=jsonObject[key].Capability.Layer;
                    sublayers.push({title:singleLayer.Name,name:singleLayer.Name,is_enabled:false,metadata:null});
                }
                layers.layers=sublayers;
            }
            break;
        case 'public_arcgis':
            if(jsonObject.layers){
                let name;
                if(publicLayers.url.includes('FeatureServer')){
                    name = publicLayers.url.replace(/.*services\/+(.*)+\/FeatureServer.*/g, "$1");
                }
                if(publicLayers.url.includes('MapServer')){
                    name = publicLayers.url.replace(/.*services\/+(.*)+\/MapServer.*/g, "$1");
                }
                layers.name=name;
                layers.metadata['title'] = layers.name;
                layers.version=jsonObject.currentVersion;
                jsonObject.layers.forEach(layer => {
                    sublayers.push({id:layer.id,title:layer.name,name:layer.name,is_enabled:false,metadata:null});
                });
                layers.layers=sublayers;
            }
            break;
        default:
            break;
    }
    return layers;
}
//open data layer map
export function openDataAccordionMap(layers): any{
    layers.map(item => {
        item.type=item.type;
        item.active=false;
        item.color=item.type.metadata ? item.type.metadata.color : "#3399CC";
        item.zoom=20;
        item.transparency=1;
        const flag=[];
        item.layers.forEach(el => {
            if(el.is_enabled){
                el.type=item.type;
                el.is_active=false;
                el.color=el.metadata ? el.metadata.color : "#3399CC";
                el.zoom=20;
                el.transparency=1;
                // eslint-disable-next-line no-unused-expressions
                el.credentials = item.credentials ? true : false,
                flag.push(el);
                item.sublayers=flag;
            }
        });
    });
    return layers;
}
//wms url genrator
export function wmsURLSet(map,enableLayers,layer,evt){
   return enableLayers[layer.values_.source.params_.LAYERS].getSource().getFeatureInfoUrl(evt.coordinate, map.getView().getResolution(), map.getView().getProjection(),
        {
            'INFO_FORMAT': 'text/xml' ,
            'QUERY_LAYERS': layer.values_.source.params_.LAYERS,
            'LAYERS':  layer.values_.source.params_.LAYERS,
            'REQUEST': 'GetFeatureInfo'
        });
}
//wms feature parce
export function wmsFeatureget(featureParce,layer){
    let feature={};
    if(featureParce.FeatureInfoResponse){
        if(featureParce.FeatureInfoResponse.FIELDS){
            if(featureParce.FeatureInfoResponse.FIELDS['@attributes']){
                feature=featureParce.FeatureInfoResponse.FIELDS['@attributes'];
                Object.assign(feature, {layer:layer.values_.source.params_.LAYERS});
            }
        }
    }
    return feature;
}
//open data url parse validation
export function urlParseParames(url,parseUrl,type){
  if(parseUrl.has('version')){
    url.searchParams.delete('version');
  }
  if(!parseUrl.has('request')){
    url.searchParams.append('request', 'GetCapabilities');
  }
  if(!parseUrl.has('service')){
    url.searchParams.append('service', type.toUpperCase());
  }
  return url.toString();
}
//set opendata layer date formate
export function openDataLayerFormate(feature): any{
  for(const [key, value] of Object.entries(feature)) {
    switch(true){
      case key.includes('LaatsteUpd'):
        feature[key]=moment(value).format('DD/MM/YYYY hh:mm');
        break;
      case key.includes('date'):
        feature[key]=moment(value).format('DD/MM/YYYY hh:mm');
        break;
    }
  }
  return feature;
}
